package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.RosterFinancials
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.team.isOngoingAt
import com.evoapps.evoshift.domain.usecase.user.PersonNames
import com.evoapps.evoshift.domain.usecase.user.ResolvePersonNamesUseCase
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

data class ManagerDashboardState(
    val monthlyStats: RosterFinancials?,
    val todayRecord: DailyRecord?,
    val pendingSwapsCount: Int,
    val openShiftsTodayCount: Int,
    val activeEmployees: List<String> = emptyList(),
    val upcomingEmployees: List<String> = emptyList(),
    val completedEmployees: List<String> = emptyList(),
    val upcomingDate: String? = null,
    /** Today's assigned shifts with clock times and status, by planned start (SH-72). */
    val todayAtWork: List<AtWorkEntry> = emptyList()
)

class GetManagerDashboardUseCase(
    private val financialRepository: FinancialRepository,
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val now: () -> Instant = { AppClock.now() },
    /** The device's zone: used only when the workplace's own zone is unknown. */
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() },
    /** For the workplace's time zone ("today" and "late" are the workplace's, not the phone's). */
    private val workplaceRepository: WorkplaceRepository? = null
) {
    /**
     * [includeFinances]: the month's money (roster_financials), which only financial managers and
     * the owner may read; without it the figures are null and nothing is asked of the server.
     */
    suspend operator fun invoke(rosterId: String, includeFinances: Boolean = true): Result<ManagerDashboardState> {
        return try {
            // 1. Pobierz finanse miesięczne
            val stats = if (includeFinances) financialRepository.getRosterFinancials(rosterId).getOrNull() else null

            // 2. Pobierz rekord dzienny ("Dzisiaj")
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            val zone = workplaceRepository?.getWorkplace(workplaceId)?.getOrNull()
                ?.let { timeZoneOrNull(it.settings.timezone) } ?: timeZone()
            val nowLocal = now().toLocalDateTime(zone)
            val today = nowLocal.date.toString()
            // Shift times are the workplace's local ISO date-times: compare as strings with its local now.
            val nowIso = nowLocal.toString()
            val todayId = DailyRecordIds.of(workplaceId, today)
            val todayRecord = financialRepository.getDailyRecord(todayId).getOrNull()

            // 3. Pobierz wszystkie zmiany z tego grafiku
            val allShiftsResult = shiftRepository.getShiftsForRoster(rosterId)
            val allShifts = allShiftsResult.getOrNull() ?: emptyList()

            // 4. Policz oczekujące swapy (wymagające akceptacji)
            val pendingSwaps = allShifts.count {
                it.swapRequest?.status == SwapStatus.TAKEN_PENDING
            }

            // 5. Zmiany na dzisiaj
            val todayShifts = allShifts.filter { it.date == today }
            val openShifts = todayShifts.count { it.userId == null }

            // Shifts carry only the assignee's id: names from the shared resolver (SH-235: the
            // team, then the profiles of people no longer on it); nobody found shows as unnamed.
            val resolveNames = ResolvePersonNamesUseCase(userRepository)
            val names = resolveNames(workplaceId, resolveNames.peopleOn(allShifts.filter { it.date >= today }))
            fun nameOf(userId: String?) = userId?.let { names[it] ?: PersonNames.UNNAMED }

            val active = mutableListOf<String>()
            val upcoming = mutableListOf<String>()
            val completed = mutableListOf<String>()
            var upcomingDateLabel: String? = null

            todayShifts.filter { it.userId != null }.sortedBy { it.plannedTime.start }.forEach { shift ->
                val name = nameOf(shift.userId)!!
                when {
                    shift.isOngoingAt(nowIso) -> active.add(name)
                    shift.actualTime?.start.isNullOrEmpty() && nowIso < shift.plannedTime.start -> upcoming.add(name)
                    else -> completed.add(name)
                }
            }

            if (upcoming.isNotEmpty()) {
                upcomingDateLabel = today
            } else {
                val futureShifts = allShifts.filter { it.date > today && it.userId != null }.sortedBy { it.date }
                val nextDate = futureShifts.firstOrNull()?.date
                if (nextDate != null) {
                    futureShifts.filter { it.date == nextDate }.forEach { shift ->
                        upcoming.add(nameOf(shift.userId)!!)
                    }
                    upcomingDateLabel = nextDate
                }
            }

            Result.success(
                ManagerDashboardState(
                    monthlyStats = stats,
                    todayRecord = todayRecord,
                    pendingSwapsCount = pendingSwaps,
                    openShiftsTodayCount = openShifts,
                    activeEmployees = active.distinct(),
                    upcomingEmployees = upcoming.distinct(),
                    completedEmployees = completed.distinct(),
                    upcomingDate = upcomingDateLabel,
                    todayAtWork = todayAtWork(allShifts, today, nowIso) { nameOf(it)!! }
                )
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
